import {
  ArrowRightIcon,
  CodeIcon,
  MessageSquareIcon,
  NetworkIcon,
} from 'lucide-react'
import { Link } from '#router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TRACK_IDENTITY } from '../data/task-config'
import { CodingGraph } from './coding-graph'
import { TalentTree } from './talent-tree'
import { TierLadder } from './tier-ladder'

/**
 * Says out loud that a track's numbers are invented. Only the coding track has
 * a real taxonomy and per-client mastery state; until system design and
 * behavioral get theirs, their views run on the sample data in `mastery-data`
 * and must not be read as progress (LG-17).
 */
function PreviewNotice() {
  return (
    <Badge variant="outline" className="border-dashed">
      Preview — sample data, not your progress
    </Badge>
  )
}

function PracticeLink({ href, label }: { href: string; label: string }) {
  return (
    <Button variant="outline" size="sm" asChild>
      <Link href={href}>
        {label}
        <ArrowRightIcon />
      </Link>
    </Button>
  )
}

export function MasterySection() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Path</CardTitle>
        <CardDescription>
          Track your mastery across the three interview pillars — what you have
          locked in, what is in progress, and what opens up next.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="coding" className="gap-5">
          <TabsList>
            <TabsTrigger
              value="coding"
              className={TRACK_IDENTITY.coding.activeTab}
            >
              <CodeIcon />
              Coding
            </TabsTrigger>
            <TabsTrigger
              value="system-design"
              className={TRACK_IDENTITY['system-design'].activeTab}
            >
              <NetworkIcon />
              System design
            </TabsTrigger>
            <TabsTrigger
              value="behavioral"
              className={TRACK_IDENTITY.behavioral.activeTab}
            >
              <MessageSquareIcon />
              Behavioral
            </TabsTrigger>
          </TabsList>

          <TabsContent value="coding" className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Your topic map — node fill shows mastery, lines show what each
                topic unlocks. Click a node to see its subtopics.
              </p>
              <PracticeLink
                href="/problems"
                label="Coding Interviews practice"
              />
            </div>
            <CodingGraph />
          </TabsContent>

          <TabsContent value="system-design" className="flex flex-col gap-5">
            <PreviewNotice />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                A five-stage climb — each stage has its own meter, and the stage
                you are working through is highlighted.
              </p>
              <PracticeLink
                href="/problems"
                label="System Design Interviews practice"
              />
            </div>
            <TierLadder />
          </TabsContent>

          <TabsContent value="behavioral" className="flex flex-col gap-5">
            <PreviewNotice />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                A story skill tree — master each tier of stories to unlock the
                next. Each skill's badge counts the stories it covers.
              </p>
              <PracticeLink
                href="/problems"
                label="Behavioral Interviews practice"
              />
            </div>
            <TalentTree />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
